import type { WithdrawalRequest } from '../types';

type WithdrawalMeta = {
  /** 0x static wallet webhook (`zeroxProcessingWebhookService`). */
  address?: string;
  payoutWalletAddress?: string;
  payoutNetwork?: string;
  addressData?: {
    address?: string;
    bitcoinAddress?: string;
    network?: string;
    memo?: string;
    bankCode?: string;
    accountNumber?: string;
    accountName?: string;
    routingNumber?: string;
    accountType?: string;
    pyusdAddress?: string;
    ecashAccount?: string;
  };
  withdrawalAddress?: {
    bankCode?: string;
    accountNumber?: string;
    accountName?: string;
    routingNumber?: string;
    accountType?: string;
    address?: string;
    bitcoinAddress?: string;
    network?: string;
    memo?: string;
    pyusdAddress?: string;
    ecashAccount?: string;
  };
};

export type WithdrawalPayoutDestination =
  | {
      kind: 'bank';
      bankCode: string | null;
      accountNumber: string | null;
      accountName: string | null;
      routingNumber: string | null;
      accountType?: string | null;
    }
  | {
      kind: 'crypto';
      address: string;
      network: string;
      memo?: string | null;
    }
  | {
      kind: 'pyusd';
      address: string;
      network: string;
    }
  | {
      kind: 'ecash';
      account: string;
      accountName?: string | null;
    };

export function getWithdrawalPayoutDestination(w: WithdrawalRequest): WithdrawalPayoutDestination | null {
  if (w.bankInfo && (w.bankInfo.bankCode || w.bankInfo.accountNumber)) {
    return {
      kind: 'bank',
      bankCode: w.bankInfo.bankCode || null,
      accountNumber: w.bankInfo.accountNumber || null,
      accountName: w.bankInfo.accountName || null,
      routingNumber: w.bankInfo.routingNumber || null,
      accountType: w.bankInfo.accountType ?? null,
    };
  }

  const bitcoinAddress = w.bitcoinInfo?.bitcoinAddress?.trim();
  if (bitcoinAddress) {
    return {
      kind: 'crypto',
      address: bitcoinAddress,
      network: w.bitcoinInfo?.bitcoinNetwork || 'mainnet',
      memo: null,
    };
  }

  if (w.pyusdInfo?.pyusdAddress) {
    return {
      kind: 'pyusd',
      address: w.pyusdInfo.pyusdAddress,
      network: w.pyusdInfo.network || 'solana',
    };
  }

  const ecash = w.ecashInfo;
  if (ecash?.ecashAccount) {
    return {
      kind: 'ecash',
      account: ecash.ecashAccount,
      accountName: ecash.accountName ?? null,
    };
  }

  const meta = (w.metadata || {}) as WithdrawalMeta;
  const syncMeta = (w.syncMetadata || {}) as WithdrawalMeta;
  const addr = meta.withdrawalAddress || syncMeta.withdrawalAddress || syncMeta.addressData;
  const portalAddress = meta.payoutWalletAddress?.trim();
  const staticWalletAddress = meta.address?.trim();

  if (staticWalletAddress) {
    return {
      kind: 'crypto',
      address: staticWalletAddress,
      network: meta.payoutNetwork === 'btc' ? 'mainnet' : meta.payoutNetwork || 'mainnet',
    };
  }

  if (portalAddress) {
    return {
      kind: 'crypto',
      address: portalAddress,
      network: meta.payoutNetwork === 'btc' ? 'mainnet' : meta.payoutNetwork || 'mainnet',
    };
  }

  if (addr?.bitcoinAddress || addr?.address) {
    return {
      kind: 'crypto',
      address: (addr.bitcoinAddress || addr.address) as string,
      network: addr.network || 'mainnet',
      memo: addr.memo ?? null,
    };
  }

  if (addr?.bankCode || addr?.accountNumber) {
    return {
      kind: 'bank',
      bankCode: addr.bankCode || null,
      accountNumber: addr.accountNumber || null,
      accountName: addr.accountName || null,
      routingNumber: addr.routingNumber || null,
      accountType: addr.accountType ?? null,
    };
  }

  if (addr?.pyusdAddress) {
    return {
      kind: 'pyusd',
      address: addr.pyusdAddress,
      network: addr.network || 'solana',
    };
  }

  if (addr?.ecashAccount) {
    return {
      kind: 'ecash',
      account: addr.ecashAccount,
      accountName: addr.accountName ?? null,
    };
  }

  return null;
}
